import type { CSSProperties } from "react";
import { cn } from "@/lib/cn.ts";

const SHAPES = ["bubble", "notch", "fold", "antenna", "tab", "sprout"] as const;
const COLORS = ["#ffad0a", "#16b8c5", "#8d4ee8", "#347ff0", "#f45861", "#2fbd69"] as const;
export type AgentGlyphShape = (typeof SHAPES)[number];
export type AgentGlyphExpression = "neutral" | "happy" | "thinking" | "wink";

function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function agentGlyphShape(seed: string): AgentGlyphShape {
  return SHAPES[hashSeed(`${seed}:shape`) % SHAPES.length]!;
}

export function agentGlyphColor(seed: string): string {
  return COLORS[hashSeed(`${seed}:color`) % COLORS.length]!;
}

export function AgentGlyph({
  seed,
  color = agentGlyphColor(seed),
  expression = "neutral",
  animated = false,
  label,
  className
}: {
  seed: string;
  color?: string;
  expression?: AgentGlyphExpression;
  animated?: boolean;
  label?: string;
  className?: string;
}) {
  const shape = agentGlyphShape(seed);
  const animationDelay = `${-(hashSeed(`${seed}:motion`) % 4_000)}ms`;
  return (
    <span
      className={cn("agent-icon", className)}
      data-animated={animated ? "true" : undefined}
      data-expression={expression}
      data-shape={shape}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ color, "--agent-icon-delay": animationDelay } as CSSProperties}
    >
      <svg viewBox="0 0 100 100" focusable="false" aria-hidden>
        <g className="agent-icon-character">
          <Shape shape={shape} />
          <Face expression={expression} />
        </g>
      </svg>
    </span>
  );
}

function Shape({ shape }: { shape: AgentGlyphShape }) {
  if (shape === "bubble") {
    return (
      <g>
        <path
          className="agent-icon-body"
          d="M45 10h32c8 0 13 5 13 13v30c0 23-17 39-40 39-24 0-42-18-42-42 0-22 16-40 37-40Z"
        />
        <circle className="agent-icon-accessory" cx="91" cy="57" r="8" fill="currentColor" />
      </g>
    );
  }
  if (shape === "notch") {
    return (
      <path
        className="agent-icon-body"
        d="M26 10h16c3 0 4 1 5 4 2 6 7 10 14 10s12-4 14-10c1-3 2-4 5-4h1c10 0 17 8 17 18v46c0 10-8 18-18 18H25C15 92 7 84 7 74V29c0-11 8-19 19-19Z"
      />
    );
  }
  if (shape === "fold") {
    return (
      <g>
        <path
          className="agent-icon-body"
          d="M55 10h18l19 20c2 2 3 5 3 8v18c0 24-15 35-43 35C24 91 8 78 8 55c0-25 20-45 47-45Z"
        />
        <path d="M72 10v17c0 7 5 12 12 12h11L72 10Z" fill="#ffffff" opacity="0.45" />
      </g>
    );
  }
  if (shape === "antenna") {
    return (
      <g>
        <path d="M82 25 92 12" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
        <circle className="agent-icon-accessory" cx="94" cy="10" r="7" fill="currentColor" />
        <path
          className="agent-icon-body"
          d="M51 21c24 0 41 15 41 37 0 20-17 36-41 36S9 78 9 57c0-21 18-36 42-36Z"
        />
      </g>
    );
  }
  if (shape === "tab") {
    return (
      <g>
        <rect x="84" y="50" width="12" height="14" rx="7" fill="#16233b" />
        <path
          className="agent-icon-body"
          d="M25 21c17-4 36-4 54 0 9 2 14 9 13 18l-3 27C87 83 72 94 52 94 29 94 12 80 10 58L8 41c-1-10 6-18 17-20Z"
        />
      </g>
    );
  }
  return (
    <g>
      <path
        className="agent-icon-accessory"
        d="M51 22V12m0 1c-1-7-8-10-12-5m12 5c2-7 9-9 13-4"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <path
        className="agent-icon-body"
        d="M27 25h49c14 0 23 10 23 24v16c0 17-13 28-30 28H31C14 93 2 82 2 65V52c0-16 10-27 25-27Z"
      />
    </g>
  );
}

function Face({ expression }: { expression: AgentGlyphExpression }) {
  if (expression === "happy") {
    return (
      <g className="agent-icon-face">
        <path
          d="M34 52c2-5 8-5 10 0M59 52c2-5 8-5 10 0"
          fill="none"
          stroke="#15213a"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <path d="M47 65c3 3 8 3 11 0" fill="none" stroke="#15213a" strokeWidth="3" strokeLinecap="round" />
      </g>
    );
  }
  if (expression === "wink") {
    return (
      <g className="agent-icon-face">
        <path
          d="M34 53c2-5 8-5 10 0"
          fill="none"
          stroke="#15213a"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <rect x="60" y="45" width="8" height="17" rx="4" fill="#15213a" />
        <path d="M47 66c3 2 8 2 11 0" fill="none" stroke="#15213a" strokeWidth="3" strokeLinecap="round" />
      </g>
    );
  }
  return (
    <g className="agent-icon-face">
      <g className="agent-icon-eyes" fill="#15213a">
        <rect x="34" y="45" width="8" height="17" rx="4" />
        <rect x="60" y="45" width="8" height="17" rx="4" />
      </g>
      {expression === "thinking" ? (
        <circle className="agent-icon-thought" cx="76" cy="68" r="3" fill="#15213a" />
      ) : null}
    </g>
  );
}
